import { useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "zedule-install-dismissed";

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isiOS(): boolean {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

/** Dismissible install affordance. Android/desktop Chromium: a real install
 *  button via beforeinstallprompt. iOS Safari: an "Add to Home Screen" hint
 *  (no prompt API). Hidden when already installed or previously dismissed. */
export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === "1",
  );

  useEffect(() => {
    if (dismissed || isStandalone()) return;
    const onBip = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    // iOS gives no event — show the hint there.
    if (isiOS()) setShowIosHint(true);
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, [dismissed]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
    setDeferred(null);
    setShowIosHint(false);
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    dismiss();
  };

  if (dismissed || isStandalone()) return null;
  if (!deferred && !showIosHint) return null;

  return (
    <div
      role="dialog"
      aria-label="Install Zedule"
      style={{
        position: "fixed",
        left: 12,
        right: 12,
        bottom: "calc(104px + env(safe-area-inset-bottom))",
        zIndex: 55,
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 14px",
        borderRadius: 14,
        background: "var(--paper-raised)",
        border: "1px solid var(--rule)",
        boxShadow: "var(--shadow-md)",
        color: "var(--ink)",
      }}
    >
      <span style={{ flex: 1, fontSize: 13.5, lineHeight: 1.4 }}>
        {deferred
          ? "Install Zedule for an app-like, full-screen experience."
          : "Install Zedule: tap the Share button, then “Add to Home Screen.”"}
      </span>
      {deferred && (
        <button type="button" className="btn-primary" onClick={() => void install()}>
          Install
        </button>
      )}
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        style={{ background: "transparent", border: "none", color: "var(--ink-soft)", cursor: "pointer", fontSize: 16 }}
      >
        ✕
      </button>
    </div>
  );
}
